// src/features/stock/components/StockMovementDetails.js
import React from 'react';
import {
  X,
  Package,
  Calendar,
  User,
  FileText,
  Hash,
  MapPin,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCw,
  AlertTriangle,
  Printer,
  Download
} from 'lucide-react';
import { format } from 'date-fns';
import { MOVEMENT_TYPES } from '../constants/stockConstants';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';

const StockMovementDetails = ({ isOpen, onClose, movement }) => {
  if (!isOpen || !movement) return null;

  const getMovementIcon = () => {
    switch (movement.type) {
      case 'in':
      case 'return':
        return <ArrowDownCircle className="h-8 w-8 text-green-600" />;
      case 'out':
      case 'damaged':
      case 'expired':
        return <ArrowUpCircle className="h-8 w-8 text-red-600" />;
      default:
        return <RefreshCw className="h-8 w-8 text-yellow-600" />;
    }
  };

  const getMovementTypeColor = () => {
    const colors = {
      in: 'bg-green-100 text-green-800',
      out: 'bg-red-100 text-red-800',
      adjustment: 'bg-yellow-100 text-yellow-800',
      return: 'bg-blue-100 text-blue-800',
      transfer: 'bg-purple-100 text-purple-800',
      damaged: 'bg-orange-100 text-orange-800',
      expired: 'bg-gray-100 text-gray-800',
    };
    return colors[movement.type] || 'bg-gray-100 text-gray-800';
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    // Implement PDF download
    console.log('Download PDF');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${
                  movement.type === 'in' ? 'bg-green-100' :
                  movement.type === 'out' ? 'bg-red-100' :
                  'bg-yellow-100'
                }`}>
                  {getMovementIcon()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">
                      Stock Movement Details
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMovementTypeColor()}`}>
                      {MOVEMENT_TYPES[movement.type?.toUpperCase()]?.name || movement.type}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Reference: {movement.reference || 'N/A'} | ID: {movement.id}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrint}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Print"
                >
                  <Printer className="h-5 w-5" />
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Download PDF"
                >
                  <Download className="h-5 w-5" />
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Item Summary */}
            <Card className="mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Item</p>
                  <p className="font-medium text-gray-900">{movement.item_name}</p>
                  <p className="text-xs text-gray-500">SKU: {movement.sku || 'N/A'}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-sm text-gray-500">Current Stock</p>
                  <p className="font-medium text-gray-900">
                    {movement.current_quantity} {movement.unit}
                  </p>
                </div>
              </div>
            </Card>

            {/* Movement Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-xs text-gray-500">Date & Time</p>
                <p className="text-sm font-medium flex items-center gap-1 mt-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {format(new Date(movement.created_at), 'MMMM dd, yyyy HH:mm')}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Quantity</p>
                <p className={`text-lg font-bold ${
                  ['in', 'return'].includes(movement.type) 
                    ? 'text-green-600' 
                    : ['out', 'damaged', 'expired'].includes(movement.type)
                    ? 'text-red-600'
                    : 'text-yellow-600'
                }`}>
                  {['in', 'return'].includes(movement.type) ? '+' : '-'}
                  {movement.quantity} {movement.unit}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Performed By</p>
                <p className="text-sm font-medium flex items-center gap-1 mt-1">
                  <User className="h-4 w-4 text-gray-400" />
                  {movement.performed_by || 'System'}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Reference</p>
                <p className="text-sm font-medium flex items-center gap-1 mt-1">
                  <Hash className="h-4 w-4 text-gray-400" />
                  {movement.reference || 'N/A'}
                </p>
              </div>
            </div>

            {/* Location Details */}
            {(movement.from_location || movement.to_location) && (
              <Card className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Location Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  {movement.from_location && (
                    <div>
                      <p className="text-xs text-gray-500">From</p>
                      <p className="text-sm flex items-center gap-1 mt-1">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {movement.from_location}
                      </p>
                    </div>
                  )}
                  {movement.to_location && (
                    <div>
                      <p className="text-xs text-gray-500">To</p>
                      <p className="text-sm flex items-center gap-1 mt-1">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {movement.to_location}
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Batch Information */}
            {(movement.batch_number || movement.expiry_date) && (
              <Card className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Batch Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  {movement.batch_number && (
                    <div>
                      <p className="text-xs text-gray-500">Batch Number</p>
                      <p className="text-sm font-medium mt-1">{movement.batch_number}</p>
                    </div>
                  )}
                  {movement.expiry_date && (
                    <div>
                      <p className="text-xs text-gray-500">Expiry Date</p>
                      <p className={`text-sm font-medium mt-1 ${
                        new Date(movement.expiry_date) < new Date() ? 'text-red-600' : ''
                      }`}>
                        {format(new Date(movement.expiry_date), 'MMMM dd, yyyy')}
                        {new Date(movement.expiry_date) < new Date() && (
                          <span className="ml-2 text-xs">(Expired)</span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Financial Information */}
            {movement.cost_per_unit && (
              <Card className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Financial Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Cost per Unit</p>
                    <p className="text-sm font-medium mt-1">${movement.cost_per_unit.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Cost</p>
                    <p className="text-sm font-medium mt-1">
                      ${(movement.cost_per_unit * movement.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Supplier Information */}
            {movement.supplier && (
              <Card className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Supplier Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500">Supplier</p>
                    <p className="text-sm font-medium mt-1">{movement.supplier}</p>
                  </div>
                  {movement.purchase_order && (
                    <div>
                      <p className="text-xs text-gray-500">Purchase Order</p>
                      <p className="text-sm font-medium mt-1">{movement.purchase_order}</p>
                    </div>
                  )}
                  {movement.invoice_number && (
                    <div>
                      <p className="text-xs text-gray-500">Invoice Number</p>
                      <p className="text-sm font-medium mt-1">{movement.invoice_number}</p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Recipient Information */}
            {(movement.recipient || movement.department) && (
              <Card className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Recipient Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  {movement.recipient && (
                    <div>
                      <p className="text-xs text-gray-500">Recipient</p>
                      <p className="text-sm font-medium mt-1">{movement.recipient}</p>
                    </div>
                  )}
                  {movement.department && (
                    <div>
                      <p className="text-xs text-gray-500">Department</p>
                      <p className="text-sm font-medium mt-1">{movement.department}</p>
                    </div>
                  )}
                  {movement.patient_id && (
                    <div>
                      <p className="text-xs text-gray-500">Patient ID</p>
                      <p className="text-sm font-medium mt-1">{movement.patient_id}</p>
                    </div>
                  )}
                  {movement.prescription_id && (
                    <div>
                      <p className="text-xs text-gray-500">Prescription ID</p>
                      <p className="text-sm font-medium mt-1">{movement.prescription_id}</p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Reason and Notes */}
            <Card className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Reason</h4>
              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                {movement.reason}
              </p>
            </Card>

            {movement.notes && (
              <Card className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Additional Notes</h4>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                  {movement.notes}
                </p>
              </Card>
            )}

            {/* Authorization */}
            {movement.authorized_by && (
              <div className="border-t pt-4 mt-4">
                <p className="text-xs text-gray-500">Authorized By</p>
                <p className="text-sm font-medium mt-1">{movement.authorized_by}</p>
              </div>
            )}

            {/* Documents */}
            {movement.documents && movement.documents.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Attached Documents</h4>
                <div className="space-y-2">
                  {movement.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{doc.name}</span>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => window.open(doc.url, '_blank')}
                      >
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockMovementDetails;